import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircle } from "@phosphor-icons/react";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ProveedorCombobox from "@/components/ProveedorCombobox";
import ClienteCombobox from "@/components/ClienteCombobox";

/** Convierte un movimiento del extracto en un pago a proveedor (egreso) o cobro de cliente (ingreso). */
export default function ConvertirMovimientoDialog({ mov, onClose, onDone }) {
  const esPago = mov?.tipo === "egreso";
  const [proveedores, setProveedores] = useState([]);
  const [contraparteId, setContraparteId] = useState("");
  const [contraparteNombre, setContraparteNombre] = useState("");
  const [concepto, setConcepto] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!mov) return;
    setContraparteId(""); setContraparteNombre(""); setConcepto("");
    if (esPago) api.get("/proveedores").then(({ data }) => setProveedores(Array.isArray(data) ? data : data.items || [])).catch(() => {});
  }, [mov, esPago]);

  const confirmar = async () => {
    if (!contraparteId) return toast.error(`Elegí un ${esPago ? "proveedor" : "cliente"}`);
    setSaving(true);
    try {
      const { data } = await api.post("/conciliacion/convertir", { mov_id: mov.id, contraparte_id: contraparteId, concepto: concepto || null });
      toast.success(`${esPago ? "Pago" : "Cobro"} registrado a ${data.contraparte_nombre} e impactado en su cuenta corriente`);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!mov} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="convertir-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[16px] flex items-center gap-2">
            <UserCircle size={18} weight="bold" className="text-[#6B1F24]" />
            {esPago ? "Registrar como pago a proveedor" : "Registrar como cobro de cliente"}
          </DialogTitle>
        </DialogHeader>
        {mov && (
          <div className="space-y-3 text-[13px]">
            <div className="border border-[#E5E5E5] bg-neutral-50 rounded-sm px-3 py-2 flex items-center justify-between gap-3 text-[12px]">
              <div className="min-w-0">
                <div className="font-mono text-[11px] text-[#666]">{fmtDate(mov.fecha)} · {mov.caja_nombre}</div>
                <div className="truncate" title={mov.concepto}>{mov.concepto}</div>
              </div>
              <div className={`font-mono font-semibold shrink-0 ${esPago ? "num-neg" : "num-pos"}`}>{esPago ? "-" : "+"} {fmtMoney(mov.monto, mov.moneda)}</div>
            </div>
            <div>
              <Label className="text-[12px]">{esPago ? "Proveedor *" : "Cliente *"}</Label>
              <div className="mt-1" data-testid="convertir-contraparte">
                {esPago ? (
                  <ProveedorCombobox proveedores={proveedores} value={contraparteId} onChange={(v) => setContraparteId(v)} />
                ) : (
                  <ClienteCombobox value={contraparteNombre} onSelect={(c) => { setContraparteId(c?.id || ""); setContraparteNombre(c?.nombre || c?.razon_social || ""); }} testId="convertir-cliente" />
                )}
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Concepto (opcional)</Label>
              <Input className="rounded-sm mt-1" value={concepto} onChange={(e) => setConcepto(e.target.value)}
                placeholder={`${esPago ? "Pago" : "Cobro"} s/ extracto · ${mov.concepto || ""}`} data-testid="convertir-concepto" />
            </div>
            <p className="text-[11px] text-neutral-600">
              Se registra un {esPago ? "pago" : "cobro"} normal por {fmtMoney(mov.monto, mov.moneda)} en la caja <b>{mov.caja_nombre}</b>: baja la cuenta corriente del {esPago ? "proveedor" : "cliente"}, genera el asiento contable y queda conciliado con este movimiento del banco (no se duplica el saldo de la caja). Después podés imputarlo a facturas desde Cuentas Corrientes.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm h-9" data-testid="convertir-cancelar">Cancelar</Button>
          <Button onClick={confirmar} disabled={!contraparteId || saving} className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]" data-testid="convertir-confirmar">
            {saving ? "Registrando…" : esPago ? "Registrar pago" : "Registrar cobro"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
